import { useState, useEffect, useCallback } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../services/firebaseService/connection";

const ChannelType = {
  PRIVATE: "private",
  PUBLIC: "public",
  GLOBAL: "global",
};

async function fetchChannels(group, userId) {
  if (!group || !userId) return [];

  const now = Math.floor(Date.now() / 1000);

  const groupsRef = collection(
    db,
    "channels",
    `${group.clubId}_channels`,
    "groups",
  );

  const queryPromises = [
    ...(group.clubPostAllowed
      ? [getDocs(query(groupsRef, where("global", "==", true)))]
      : []),

    getDocs(
      query(
        groupsRef,
        where("groupId", "==", group.id),
        where("channelType", "==", ChannelType.PRIVATE),
        where("memberIds", "array-contains", userId),
      ),
    ),

    getDocs(
      query(
        groupsRef,
        where("groupId", "==", group.id),
        where("channelType", "==", ChannelType.PUBLIC),
        where("endDate", ">=", now),
      ),
    ),
  ];

  const snapshots = await Promise.all(queryPromises);
  const channels = [];

  for (const snapshot of snapshots) {
    for (const docSnap of snapshot.docs) {
      const channel = { id: docSnap.id, ...docSnap.data() };
      if (
        channel.channelType === ChannelType.GLOBAL &&
        !group.clubPostAllowed
      ) {
        continue;
      }
      channels.push(channel);
    }
  }

  return channels;
}

export function useChannels(group, user) {
  const {
    profile: { uid: userId },
  } = user;
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!group || !user) return;

    setLoading(true);
    setError(null);

    try {
      const result = await fetchChannels(group, userId);
      setChannels(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const matchThreadChannels = channels
    .filter(
      (c) =>
        c.channelType === ChannelType.PUBLIC && c.channelName !== "Global Chat",
    )
    .map((c) => ({ label: c.channelName, value: c.id }));

  return {
    channels, // all channels
    matchThreadChannels, // public only, for match thread
    loading,
    error,
    refetch: load, // manual refetch if needed
  };
}
